import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextField } from '../../../../components/inputs/TextField';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { maintenanceCategoriesApi } from '../buildingManagementApi';
import type { MaintenanceCategoryListItem } from '../buildingManagementTypes';
import { sampleMaintenanceCategory } from '../utils/sampleBuildingManagement';

/** The server's bounds on a window, in days. */
const MIN_DAYS = 1;
const MAX_DAYS = 365;

/**
 * Create or edit one maintenance category.
 *
 * **The window is OPTIONAL and empty means the settings row's.** A category that never says
 * falls back to the one Near Expiration window under Settings › Aging; one that does wins over
 * it for every item filed under it.
 */
export function MaintenanceCategoryModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: MaintenanceCategoryListItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();

  // Seeded once, on mount — the parent mounts this only while it is open.
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [nearDays, setNearDays] = useState(
    editing?.nearExpirationDays != null ? String(editing.nearExpirationDays) : '',
  );

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const parsedDays = nearDays.trim() === '' ? null : Number(nearDays);
  const daysValid =
    parsedDays === null || (Number.isInteger(parsedDays) && parsedDays >= MIN_DAYS && parsedDays <= MAX_DAYS);

  const valid = name.trim().length > 0 && daysValid;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      name: name.trim(),
      description: description.trim() || null,
      nearExpirationDays: parsedDays,
    };

    try {
      if (editing) await maintenanceCategoriesApi.update(editing.id, request);
      else await maintenanceCategoriesApi.create(request);

      toast.success(editing ? `Saved ${request.name}.` : `Added ${request.name}.`);
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the category.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'maintenance-category' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit category' : 'New category'}
      size="lg"
      id="maintenance-category-modal"
      autogenerate={{
        onGenerate: () => {
          const sample = sampleMaintenanceCategory();
          setName(sample.name);
          setDescription(sample.description ?? '');
          setNearDays(sample.nearExpirationDays === null ? '' : String(sample.nearExpirationDays));
        },
        disabled: saving,
      }}
      confirm={{
        id: 'maintenance-category-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="maintenance-category-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="maintenance-category-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id="maintenance-category-name"
          label="Name"
          required
          description="What the kind of obligation is called, e.g. Permits or Elevators. Held once among the live rows."
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={150}
          disabled={saving}
          error={error?.fieldError('name')}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="maintenance-category-near-expiration-days"
            label="Near Expiration window (days)"
            type="number"
            inputMode="numeric"
            min={MIN_DAYS}
            max={MAX_DAYS}
            description={`How many days before its due date an item in this category reads as Near Expiration. Leave it empty to use the window under Settings › Aging. ${MIN_DAYS}–${MAX_DAYS}.`}
            value={nearDays}
            onChange={(e) => setNearDays(e.target.value)}
            disabled={saving}
            className="font-mono"
            error={
              daysValid
                ? error?.fieldError('nearExpirationDays')
                : `Give a whole number between ${MIN_DAYS} and ${MAX_DAYS}, or leave it empty.`
            }
          />
        </div>

        <TextField
          id="maintenance-category-description"
          label="Description"
          description="Optional note for whoever maintains the catalog."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          disabled={saving}
          error={error?.fieldError('description')}
        />
      </div>
    </Modal>
  );
}
